import cx from 'classnames';
import {useCallback} from 'react';
import {components} from 'react-select';
import {Select} from '~library/atoms/Select';
import {charcoal, silver} from '~libSass/base/_exports.sass';
import {useEmailPreview} from '~/common/fabric/Card/components/EventPreview/hooks/useEmailPreview';
import {usePreviewInvitationCreateFlow} from '~/common/fabric/Card/components/EventPreview/hooks/usePreviewInvitationCreateFlow';
import {Typography} from '~library/atoms/Typography';

import {EyeIcon} from '~library/svg/EyeIcon';
import {ClickOutLineIcon} from '~library/svg/ClickOutLineIcon';

import './PreviewSelect.sass';

const SELECT_OPTIONS = [
  {
    label: 'Preview as Guest',
    value: 'preview',
    icon: <EyeIcon className="review-content__preview-btn-icon" ratio={0.438} />,
  },
  {label: 'Email Me a Preview', value: 'email'},
];

const ICONS = {
  preview: <EyeIcon className="review-content__preview-btn-icon" ratio={0.438} />,
  email: <ClickOutLineIcon className="review-content__email-preview-btn-icon" ratio={0.438} />,
};

const selectStyle = {
  control: {
    fontSize: '0.875rem',
    width: 'fit-content',
    margin: 0,
    border: `0.5px solid ${charcoal}`,
    maxHeight: '32px',
    minHeight: '32px',
    backgroundColor: 'transparent',
    paddingRight: 0,
  },
  valueContainer: {padding: 0},
  placeholder: {
    marginLeft: 0,
    marginRight: '0.25rem',
    overflow: 'unset',
    color: charcoal,
    fontWeight: 500,
  },
  container: {width: 'fit-content', maxHeight: '36px'},
  singleValue: {
    marginLeft: 0,
    marginRight: '0.25rem',
  },
  menu: {
    marginTop: 0,
    borderRadius: '0.25rem',
    width: 'auto',
    right: 0,
  },
  menuList: {
    borderRadius: '0.25rem',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderBottom: `${silver} solid 0.0625rem`,
    '&:last-child': {border: 'none'},
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
};

const Option = ({label, value, ...optionProps}) => (
  <components.Option {...optionProps} className="select__option">
    {ICONS[value]}
    <Typography
      as="span"
      variant="paragraph2"
      id={label}
      data-qa-id={`${label.replaceAll(' ', '_')}_suggestion`}
    >
      {label}
    </Typography>
  </components.Option>
);

export const PreviewSelectContainer = ({onChange, className, hoverEffect, style}) => (
  <Select
    options={SELECT_OPTIONS}
    customStyles={{...selectStyle, ...style}}
    placeholder="Preview"
    data-qa-id="preview-event-select"
    onChange={onChange}
    value="none"
    components={{Option}}
    className={cx(className, {
      'preview-select-colored': hoverEffect,
    })}
    icon={<EyeIcon ratio={0.438} />}
  />
);

export const PreviewSelect = ({eventId, hoverEffect, className, style}) => {
  const {sendEmailPreview} = useEmailPreview({eventId});
  const {onPreviewInvitationClick} = usePreviewInvitationCreateFlow({eventId});

  const onChangeHandler = useCallback(async (value) => {
    if (value === 'email') {
      sendEmailPreview();
    } else {
      onPreviewInvitationClick();
    }
  }, []);

  return (
    <PreviewSelectContainer
      style={style}
      onChange={onChangeHandler}
      className={className}
      hoverEffect={hoverEffect}
    />
  );
};

PreviewSelect.defaultProps = {
  style: {},
};
